// ui/Modal — the single canonical modal shell.
// Owns the overlay, panel, header (title/subtitle/status/close), optional tab row,
// scrollable body, and optional footer. Portals into #modal-root. Closes on overlay
// click and Escape. No consumer should ever hand-roll .popup-* / overlay CSS again.
//
// variant="window" (the customer window, FieldRoutes-style): a movable window,
// max 1120×760 (90vw × 88vh), centred on first open, dragged by its header, its
// position remembered for the session under `windowKey`. The scrim dims the page to
// ~40% so what's behind stays readable; only Esc and × close it (a drag released
// over the scrim must never close it). `actions` sit in the header on desktop and in
// a bottom dock on a phone, where the window becomes a full-screen sheet.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";
import "./modal.css";

const PHONE_QUERY = "(max-width: 640px)";
const KEEP_VISIBLE = 160; // px of the window that always stays on screen while dragging
const HEADER_REACH = 56; // the header can never be dragged out of reach

function useIsPhone() {
  const get = () => typeof window !== "undefined" && !!window.matchMedia?.(PHONE_QUERY).matches;
  const [isPhone, setIsPhone] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE_QUERY);
    if (!mq) return undefined;
    const on = () => setIsPhone(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  return isPhone;
}

function clampPos(pos, el) {
  if (!pos || !el) return pos;
  const w = el.offsetWidth;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.min(Math.max(pos.x, KEEP_VISIBLE - w), vw - KEEP_VISIBLE),
    y: Math.min(Math.max(pos.y, 0), vh - HEADER_REACH),
  };
}

const storeKey = (k) => `abon.window.${k}`;
function readPos(k) {
  try {
    const v = JSON.parse(sessionStorage.getItem(storeKey(k)) || "null");
    return v && Number.isFinite(v.x) && Number.isFinite(v.y) ? v : null;
  } catch {
    return null;
  }
}
function writePos(k, pos) {
  try {
    sessionStorage.setItem(storeKey(k), JSON.stringify(pos));
  } catch {
    /* private window / blocked storage: the window still works, it just re-centres */
  }
}

/**
 * @param {boolean}  open
 * @param {() => void} onClose
 * @param {string}   title
 * @param {string}   [subtitle]
 * @param {React.ReactNode} [meta]   window variant: one-row facts beside the title
 * @param {string}   [status]        optional pill text in the header
 * @param {React.ReactNode} [headerActions]  optional extra header controls (left of close)
 * @param {React.ReactNode} [actions] window variant: header buttons on desktop, bottom dock on phone
 * @param {React.ReactNode} [banner]  a strip under the header, above the tabs (the customer window's at-risk banner)
 * @param {Array<{id,label,active,onClick,disabled}>} [tabs]  optional tab row
 * @param {React.ReactNode} [footer]  optional footer content (usually buttons)
 * @param {"wide"|"default"|"compact"} [size]
 * @param {"modal"|"window"} [variant]
 * @param {string}   [windowKey]     window variant: session key for the remembered position
 * @param {string}   [className]     extra class on the panel (consumer scope hook)
 * @param {boolean}  [closeOnOverlay=true]  (always false for the window variant)
 */
export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  meta,
  status,
  headerActions,
  actions,
  banner,
  tabs,
  footer,
  size = "wide",
  variant = "modal",
  windowKey = "default",
  className = "",
  closeOnOverlay = true,
  children,
}) {
  const isWindow = variant === "window";
  const isPhone = useIsPhone();
  const movable = isWindow && !isPhone;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Keep the active tab fully visible: the tab row scrolls horizontally
  // (overflow-x:auto), but at phone width the active tab can sit clipped at an
  // edge. Scroll it into view whenever it changes.
  const activeTabRef = useRef(null);
  const activeTabId = tabs && tabs.length ? (tabs.find((t) => t.active) || {}).id : null;
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [activeTabId]);

  // ── window: position (null = centred by the overlay's flexbox) ──
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);

  useLayoutEffect(() => {
    if (!open || !movable) return;
    const saved = readPos(windowKey);
    if (saved) setPos(clampPos(saved, panelRef.current));
  }, [open, movable, windowKey]);

  useEffect(() => {
    if (!open || !movable) return undefined;
    const onResize = () => setPos((p) => clampPos(p, panelRef.current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, movable]);

  const onHeaderPointerDown = useCallback(
    (e) => {
      if (!movable || e.button !== 0) return;
      if (e.target.closest("button, a, input, select, textarea, [data-nodrag]")) return;
      const rect = panelRef.current.getBoundingClientRect();
      dragRef.current = { px: e.clientX, py: e.clientY, x: rect.left, y: rect.top };
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setDragging(true);
      e.preventDefault();
    },
    [movable]
  );
  const onHeaderPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    setPos(clampPos({ x: d.x + e.clientX - d.px, y: d.y + e.clientY - d.py }, panelRef.current));
  }, []);
  const onHeaderPointerUp = useCallback(
    (e) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      setDragging(false);
      setPos((p) => {
        if (p) writePos(windowKey, p);
        return p;
      });
    },
    [windowKey]
  );

  const root = typeof document !== "undefined" ? document.getElementById("modal-root") : null;
  if (!open || !root) return null;

  const overlayClose = closeOnOverlay && !isWindow;
  const panelStyle = movable && pos ? { position: "fixed", left: pos.x, top: pos.y, margin: 0 } : undefined;
  const panelClass = [
    "abon-modal__panel",
    isWindow ? "abon-modal__panel--window" : `abon-modal__panel--${size}`,
    isWindow && isPhone ? "is-phone" : "",
    movable ? "is-movable" : "",
    dragging ? "is-dragging" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const closeBtn = (
    <button type="button" className="abon-modal__close" onClick={onClose} aria-label="Close">
      <X size={18} />
    </button>
  );

  return ReactDOM.createPortal(
    <div
      className={`abon-modal__overlay ${isWindow ? "abon-modal__overlay--window" : ""}`}
      onClick={(e) => {
        if (overlayClose && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        className={panelClass}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="abon-modal__header"
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          onPointerCancel={onHeaderPointerUp}
        >
          <div className="abon-modal__heading">
            <div className="abon-modal__titlerow">
              <h2 className="abon-modal__title">{title}</h2>
              {isWindow && status ? <span className="abon-modal__status">{status}</span> : null}
            </div>
            {subtitle ? <span className="abon-modal__subtitle">{subtitle}</span> : null}
            {meta ? <div className="abon-modal__meta">{meta}</div> : null}
          </div>
          <div className="abon-modal__header-actions">
            {!isWindow && status ? <span className="abon-modal__status">{status}</span> : null}
            {headerActions}
            {isWindow && !isPhone && actions ? <div className="abon-modal__actions">{actions}</div> : null}
            {closeBtn}
          </div>
        </div>

        {banner ? <div className="abon-modal__banner">{banner}</div> : null}

        {tabs && tabs.length ? (
          <div className="abon-modal__tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                ref={tab.active ? activeTabRef : null}
                type="button"
                role="tab"
                aria-selected={!!tab.active}
                className={`abon-modal__tab ${tab.active ? "is-active" : ""}`}
                onClick={tab.onClick}
                disabled={tab.disabled}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="abon-modal__body">{children}</div>

        {isWindow && isPhone && actions ? <div className="abon-modal__dock">{actions}</div> : null}
        {footer ? <div className="abon-modal__footer">{footer}</div> : null}
      </div>
    </div>,
    root
  );
}
